"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { FiTarget, FiTrash2, FiArrowUp, FiArrowDown } from "react-icons/fi";
import { authFetch } from "@/lib/authFetch";
import { createLogger } from "@/lib/logger";
import { marketPageUrl } from "@/lib/source-url";
import { useT } from "@/hooks/useT";
import { formatCents } from '@/lib/format-cents';

const logger = createLogger("PriceAlertsSection");

interface PriceAlert {
  id: string;
  market_title: string;
  market_slug: string;
  market_image?: string | null;
  event_slug?: string | null;
  outcome: "yes" | "no";
  condition: "above" | "below";
  target_price: number;
  status: string;
  created_at: string;
  source?: "polymarket" | "kalshi";
}

export default function PriceAlertsSection() {
  const t = useT();
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [count, setCount] = useState(0);
  const [max, setMax] = useState(5);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/price-alerts");
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.data?.alerts || []);
        setCount(data.data?.count || 0);
        setMax(data.data?.max || 5);
      }
    } catch (err) {
      logger.error("Failed to fetch price alerts", { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (alertId: string) => {
    setDeletingId(alertId);
    try {
      const res = await authFetch(`/api/price-alerts/${alertId}`, { method: "DELETE" });
      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        setCount(prev => prev - 1);
      } else {
        logger.error("Failed to delete price alert", { alertId, status: res.status });
      }
    } catch (err) {
      logger.error("Failed to delete price alert", { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="text-center py-16">
        <FiTarget className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <h3 className="font-inter-tight font-bold text-base text-gray-900 dark:text-white mb-1">{t("portfolio.alerts.emptyTitle")}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t("portfolio.alerts.emptySubtitle")}
        </p>
        <Link
          href="/polymarket/markets"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-bold text-sm rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
        >
          {t("portfolio.alerts.browseMarkets")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("portfolio.alerts.activeOf", { count, max })}
        </p>
      </div>

      {/* Alerts list */}
      <div className="space-y-2">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="p-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors"
          >
            {/* Title row with delete button */}
            <div className="flex items-start gap-3">
              {/* Market image */}
              <Link
                href={marketPageUrl({
                  marketSlug: alert.market_slug,
                  eventSlug: alert.event_slug,
                  outcome: alert.outcome,
                  source: alert.source || 'polymarket',
                })}
                className="flex-shrink-0"
              >
                <div className="w-10 h-10 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12]">
                  {alert.market_image ? (
                    <img
                      src={alert.market_image}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiTarget className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                </div>
              </Link>

              {/* Title and details */}
              <div className="flex-1 min-w-0">
                <Link
                  href={marketPageUrl({
                    marketSlug: alert.market_slug,
                    eventSlug: alert.event_slug,
                    outcome: alert.outcome,
                    source: alert.source || 'polymarket',
                  })}
                  className="block hover:opacity-80 transition-opacity"
                >
                  <p className="font-inter-tight font-medium text-sm text-gray-900 dark:text-white leading-snug">
                    {alert.market_title}
                  </p>
                </Link>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    alert.outcome === "yes"
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-red-500/10 text-red-600 dark:text-red-400"
                  }`}>
                    {alert.outcome?.toUpperCase()}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    {alert.condition === "above" ? (
                      <FiArrowUp className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <FiArrowDown className="w-3 h-3 text-red-500" />
                    )}
                    <span className="font-mono">{formatCents(Number(alert.target_price), 2)}</span>
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(alert.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Delete button */}
              <button
                onClick={() => handleDelete(alert.id)}
                disabled={deletingId === alert.id}
                className="p-2 hover:bg-red-500/10 rounded-[5px] transition-colors flex-shrink-0 -mt-1 -mr-1"
              >
                {deletingId === alert.id ? (
                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FiTrash2 className="w-4 h-4 text-gray-400 hover:text-red-500 transition-colors" />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
